/**
 * Profil choisi sur l'accueil `/decouverte` (élève, parent, prof ou curieux), AVANT tout
 * compte. Mémorisé sur l'appareil pour que la suite du parcours ne repose pas la
 * question : l'écran d'inscription (`/login?inscription=1`) prend le bon titre,
 * et l'onboarding présélectionne la carte de rôle correspondante.
 *
 * Confort, jamais une contrainte : localStorage inaccessible (Safari privé) →
 * on ne retient rien et l'onboarding repose simplement la question.
 */

export type DecouverteProfile = 'student' | 'parent' | 'tutor' | 'player';

export const DECOUVERTE_PROFILES: readonly DecouverteProfile[] = [
  'student',
  'parent',
  'tutor',
  'player',
];

const STORAGE_KEY = 'jeraisonne_decouverte_profil';

export function isDecouverteProfile(value: unknown): value is DecouverteProfile {
  return value === 'student' || value === 'parent' || value === 'tutor' || value === 'player';
}

export function readDecouverteProfile(): DecouverteProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isDecouverteProfile(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function saveDecouverteProfile(profile: DecouverteProfile): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, profile);
  } catch {
    /* Silencieux — localStorage peut être désactivé. */
  }
}

export function clearDecouverteProfile(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Rien à effacer. */
  }
}

/** Titre de l'écran d'inscription selon le profil choisi. PUR. */
export function signupTitleFor(profile: DecouverteProfile | null): string {
  if (profile === 'parent') return 'Crée ton espace parent';
  if (profile === 'tutor') return 'Crée ton espace prof';
  if (profile === 'student') return 'Crée ton compte élève';
  return 'Crée ton compte';
}

/** Sous-titre rassurant de l'écran d'inscription selon le profil. PUR. */
export function signupSubtitleFor(profile: DecouverteProfile | null): string {
  if (profile === 'tutor') return '7 jours d’essai gratuit, sans carte bancaire ni mot de passe.';
  return 'Gratuit, sans carte bancaire ni mot de passe : un code par email suffit.';
}
